import { useState, useEffect } from 'react';
import useText from '@/hooks/useText';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import { Autoplay } from 'swiper/modules';
import { releasesApiService } from '../../services/releasesApiService';
import { apiReleaseToVersionsArray } from '../About/releaseApiMappers';
import { compareVersions } from '../About/helpers';
import type { StatsJSON } from '../About/types';

export default function HomeStats() {
  const labelPublications = useText('home.stats.label_publications');
  const labelEvidences = useText('home.stats.label_evidences');
  const labelGDADiseases = useText('home.stats.label_gda_diseases');
  const labelDiseaseGenes = useText('home.stats.label_disease_genes');
  const labelDiseaseVariants = useText('home.stats.label_disease_variants');
  const labelCitedPapers = useText('home.stats.label_cited_papers');
  const labelDiseaseDiseaseAssociations = useText('home.stats.disease_disease_associations');

  const defaultStats = [
    { value: '1.6M+', label: labelPublications, iconSrc: '/static/images/icons/papers.svg' },
    { value: '13.3M+', label: labelEvidences, iconSrc: '/static/images/icons/evidences.svg' },
    { value: '38.2K+', label: labelGDADiseases, iconSrc: '/static/images/icons/iconsPink/013.svg' },
    { value: '30.4K+', label: labelDiseaseGenes, iconSrc: '/static/images/icons/diseases-traits.svg' },
    { value: '1.7M+', label: labelDiseaseVariants, iconSrc: '/static/images/icons/disease-variants.svg' },
    {
      value: '24.4M+',
      label: labelDiseaseDiseaseAssociations,
      iconSrc: '/static/images/icons/iconsPink/059.svg',
    },
  ];

  const [stats, setStats] = useState(defaultStats);

  useEffect(() => {
    let cancelled = false;

    async function loadLatestStats() {
      try {
        const statsRes = await fetch('/stats.json');
        const statsData: StatsJSON = statsRes.ok ? ((await statsRes.json()) as StatsJSON) : { current: '' };

        const statsVersions = Object.keys(statsData).filter(k => k !== 'current');
        const apiReleasesList = await releasesApiService.getReleasesList();
        const apiVersions = apiReleasesList.map(r => r.version);

        const allVersions = [...new Set([...statsVersions, ...apiVersions])];
        if (allVersions.length === 0) return;

        const latestVersion = allVersions.sort((a, b) => compareVersions(b, a))[0];

        // Try stats.json first; if the latest isn't present there, fallback to API.
        const s = statsData?.[latestVersion as string];
        let statistics = (s && typeof s !== 'string' ? s.statistics : undefined) as any;

        if (!statistics) {
          const latestRelease = await releasesApiService.getRelease(latestVersion);
          const mapped = apiReleaseToVersionsArray(latestRelease);
          statistics = mapped.statistics as any;
        }
        if (!statistics) return;
        if (cancelled) return;

        const format = (num: number | undefined, suffix = '+') => {
          const n = num ?? 0;
          if (n <= 0) return null;
          if (n >= 1_000_000) {
            const v = n / 1_000_000;
            return `${v % 1 === 0 ? v.toFixed(0) : v.toFixed(1)}M${suffix}`;
          }
          if (n >= 1_000) return `${Math.floor(n / 1_000)}K${suffix}`;
          return `${n}${suffix}`;
        };

        setStats([
          {
            value: format(statistics.publication ?? 0) ?? defaultStats[0].value,
            label: labelPublications,
            iconSrc: defaultStats[0].iconSrc,
          },
          {
            value: format(statistics.evidence ?? 0) ?? defaultStats[1].value,
            label: labelEvidences,
            iconSrc: defaultStats[1].iconSrc,
          },
          {
            value: format(statistics.disease ?? statistics.gda_disease ?? 0) ?? defaultStats[2].value,
            label: labelGDADiseases,
            iconSrc: defaultStats[2].iconSrc,
          },
          {
            value: format(statistics.gda_gene ?? statistics.gene ?? 0) ?? defaultStats[3].value,
            label: labelDiseaseGenes,
            iconSrc: defaultStats[3].iconSrc,
          },
          {
            value: format(statistics.vda_variant ?? statistics.variant ?? 0) ?? defaultStats[4].value,
            label: labelDiseaseVariants,
            iconSrc: defaultStats[4].iconSrc,
          },
          {
            value: format(statistics.dda_summary ?? statistics.dda ?? 0) ?? defaultStats[5].value,
            label: labelDiseaseDiseaseAssociations,
            iconSrc: defaultStats[5].iconSrc,
          },
        ]);
      } catch (e) {
        console.error('Error loading HomeStats:', e);
      }
    }

    loadLatestStats();
    return () => {
      cancelled = true;
    };
  }, [labelPublications, labelDiseaseGenes, labelCitedPapers, labelEvidences, labelGDADiseases]);

  return (
    <div className="home-stats-container">
      <Swiper
        modules={[Autoplay]}
        spaceBetween={10}
        slidesPerView={4}
        loop={true}
        autoplay={{ delay: 3000 }}
        breakpoints={{
          320: { slidesPerView: 2 },
          640: { slidesPerView: 3 },
          1024: { slidesPerView: 4 },
        }}
      >
        {stats.map(({ value, label, iconSrc }) => (
          <SwiperSlide key={label}>
            <div className="home-stats-card home-stats-card-row">
              <div className="home-stats-icon-wrap">
                <img src={iconSrc} className="home-stats-image-icon" alt={label} />
              </div>
              <div className="home-stats-text">
                <div className="home-stats-value">{value}</div>
                <div className="home-stats-label">{label}</div>
              </div>
            </div>
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  );
}
